import { MouseEvent, useCallback, useState } from 'react';

import { useNavigate } from 'react-router';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { DatatableWrapper, TableBody, TableColumnType, TableHeader } from 'react-bs-datatable';
import { Button, Stack, Table } from 'react-bootstrap';

import ConfirmDialog from '@components/Modals/ConfirmDialog';
import { DraftOrderNameCell } from '@components/DraftOrder/DraftOrderNameCell';
import InfoIconButton from '@components/Button/InfoIconButton';
import ExpandableSection from '@components/ExpandableSection/ExpandableSection';
import InfoDialog from '@components/Modals/InfoDialog';
import Placeholder from '@components/Placeholder/Placeholder';
import { getDraftRequisitionDetailsPath } from '@router/paths';
import useToast from '@contexts/useToast';
import { Clinician } from '@customTypes/clinician';
import { IDraftOrder } from '@customTypes/draftOrder';
import { ToastType } from '@customTypes/toast';
import { getClinicianName } from '@helpers/clinician';
import { deleteDraftOrder } from '@store/actions';
import NoDraftsContent from '../NoDraftsContent/NoDraftsContent';

interface IProps {
  draftOrders: IDraftOrder[];
  loading: boolean;
  refetchDraftOrders: () => Promise<void>;
}

export default function Drafts({ loading, draftOrders, refetchDraftOrders }: IProps) {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [showInfoDialog, setShowInfoDialog] = useState<boolean>(false);
  const [draftOrderToRemove, setDraftOrderToRemove] = useState<IDraftOrder | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const onDraftOrderNameAction = useCallback(
    (evt: MouseEvent, draftOrder: IDraftOrder) => {
      evt.preventDefault();
      navigate(getDraftRequisitionDetailsPath(draftOrder.identifier));
    },
    [navigate],
  );

  const headers: TableColumnType<IDraftOrder>[] = [
    {
      title: 'Draft Name',
      prop: 'name',
      isSortable: true,
      alignment: { horizontal: 'left' },
      cell: row => (
        <DraftOrderNameCell draft={row}>
          <Button
            as='a'
            variant='link'
            size='sm'
            className='fs-6 ps-0 text-decoration-none'
            onClick={evt => onDraftOrderNameAction(evt, row)}
            data-openreplay-sanitize
          >
            {row.name}
          </Button>
        </DraftOrderNameCell>
      ),
    },
    {
      title: 'Ordering Provider',
      prop: 'ordering_provider',
      cell: (row: IDraftOrder) => getClinicianName(row.ordering_provider),
      isSortable: true,
      alignment: { horizontal: 'left' },
    },
    {
      title: 'Created By',
      prop: 'created_by',
      cell: (row: IDraftOrder) => getClinicianName(row.created_by),
      isSortable: true,
      alignment: { horizontal: 'left' },
    },
    {
      title: 'Last Saved',
      prop: 'updated_at',
      cell: row => dayjs.utc(row.updated_at).local().format('MM/DD/YYYY'),
      isSortable: true,
      alignment: { horizontal: 'left' },
    },
    {
      title: 'Delete',
      prop: 'identifier',
      // classes below are important! Makes delete button align nicely with text in other cols
      cellProps: { className: 'align-middle py-0' },
      cell: row => (
        <Button
          size='sm'
          variant='ghost'
          className='delete-btn'
          aria-label={`Delete ${row.name}`}
          onClick={() => setDraftOrderToRemove(row)}
        >
          <FontAwesomeIcon icon={faTrashCan} className='text-danger' fontSize={16} />
        </Button>
      ),
    },
  ];

  const onDeleteDraftOrder = async () => {
    try {
      setIsProcessing(true);

      await deleteDraftOrder(draftOrderToRemove!.identifier);
      await refetchDraftOrders();
      addToast({ headerTitle: 'Draft order deleted.', type: ToastType.DEFAULT });

      setDraftOrderToRemove(null);
    } catch (err) {
      console.error('Error on deleting a draft order: ', err);
    } finally {
      setIsProcessing(false);
      setDraftOrderToRemove(null);
    }
  };

  return (
    <>
      <ExpandableSection
        id='drafts'
        expandableContentId='draftsContent'
        headerTitle={
          <>
            <span>Drafts ({draftOrders.length})</span>
            <InfoIconButton onClick={() => setShowInfoDialog(true)} aria-label='Drafts Info Button' />
          </>
        }
        loading={loading}
      >
        {loading ? (
          <Table className='table-with-sticky-header' responsive>
            <thead>
              <tr>
                {headers.map(header => (
                  <th key={header.prop}>{header.title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, index) => (
                <tr key={index}>
                  {headers.map(header => (
                    <td key={header.prop}>
                      <Placeholder size='sm' />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        ) : draftOrders.length === 0 ? (
          <NoDraftsContent content='You have no orders saved as drafts' />
        ) : (
          <DatatableWrapper
            body={draftOrders}
            headers={headers}
            sortProps={{
              initialState: { prop: 'updated_at', order: 'desc' },
              columnValueProcessor: {
                name: (value: string) => value.toLowerCase(),
                ordering_provider: (clinician: Clinician) => getClinicianName(clinician),
                created_by: (clinician?: Clinician) => getClinicianName(clinician),
              },
            }}
          >
            <Table className='table-with-sticky-header' responsive>
              <TableHeader />
              <TableBody />
            </Table>
          </DatatableWrapper>
        )}
      </ExpandableSection>

      {showInfoDialog && (
        <InfoDialog onClose={() => setShowInfoDialog(false)} title='Drafts'>
          <Stack gap={4}>
            <p className='mb-0'>
              When you edit a draft, you will arrive at the beginning of the order form. Your saved form entries will be
              revalidated against our current requirements. Some fields may no longer be valid or exist as our
              requirements evolve.
            </p>
            <p className='mb-0'>
              Drafts labeled <strong>Reanalysis</strong> continue a reanalysis order from an existing requisition.
            </p>
            <p className='mb-0'>
              If the draft is submitted as an order, it will be moved into the Completed Drafts section.
            </p>
          </Stack>
        </InfoDialog>
      )}

      {!!draftOrderToRemove && (
        <ConfirmDialog
          title='Confirm Deletion'
          description={`Do you want to delete draft order ${draftOrderToRemove.name}?`}
          onClose={() => setDraftOrderToRemove(null)}
          onConfirm={onDeleteDraftOrder}
          confirmBtnProps={{ disabled: isProcessing }}
          cancelBtnProps={{ disabled: isProcessing }}
        />
      )}
    </>
  );
}
